import type { SubMenuBlockProps } from "./types";

import { useEffect, useRef, useState, useCallback } from "react";
import { Button, Link } from "@digdir/designsystemet-react";
import { ChevronLeftIcon, ChevronRightIcon } from "@navikt/aksel-icons";

export type { SubMenuBlockProps };

const SCROLL_MARGIN_PX = 1;

const SubMenuBlock = ({ menupages }: SubMenuBlockProps) => {
  const subMenuRef = useRef<HTMLUListElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const updateArrows = useCallback(() => {
    const ref = subMenuRef.current;
    if (!ref) return;
    const { scrollLeft, scrollWidth, clientWidth } = ref;
    setShowLeftArrow(scrollLeft > SCROLL_MARGIN_PX);
    setShowRightArrow(
      scrollLeft + clientWidth < scrollWidth - SCROLL_MARGIN_PX
    );
  }, []);

  useEffect(() => {
    const ref = subMenuRef.current;
    if (!ref) return;

    updateArrows();
    ref.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);

    return () => {
      ref.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [menupages, updateArrows]);

  const scrollPage = useCallback((dir: "left" | "right") => {
    const ref = subMenuRef.current;
    if (!ref) return;
    const scrollAmount = Math.max(150, Math.floor(ref.clientWidth * 0.8));
    ref.scrollBy({
      left: dir === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  }, []);

  return (
    <nav className="full-bleed py-2 border-b border-neutralNsf-subtle submenu lg:py-4">
      <div className="content relative flex justify-center px-4">
        {/* Left arrow + gradient overlay */}
        {showLeftArrow && (
          <>
            <Button
              icon
              aria-label="Bla til venstre"
              onClick={() => scrollPage("left")}
              className="absolute left-0 top-0 min-h-10 min-w-10 h-full w-10 z-10 flex items-center justify-center border-r border-neutralNsf-subtle rounded-r-none rounded-l-3xl bg-white group hover:bg-neutralNsf-surface-hover active:bg-neutralNsf-surface-active sm:-left-8 sm:w-12"
            >
              <ChevronLeftIcon
                aria-hidden="true"
                className="shrink-0 text-neutralNsf-subtle group-hover:text-neutralNsf group-active:text-neutralNsf"
              />
            </Button>
            <div className="absolute left-10 w-8 h-10 z-10 bg-gradient-to-l from-transparent to-white pointer-events-none sm:left-4 sm:w-10 lg:h-12" />
          </>
        )}

        {/* Right arrow + gradient overlay  */}
        {showRightArrow && (
          <>
            <Button
              icon
              aria-label="Bla til høyre"
              onClick={() => scrollPage("right")}
              className="absolute right-0 top-0 min-h-10 min-w-10 h-full w-10 z-10 flex items-center justify-center border-l border-neutralNsf-subtle rounded-l-none rounded-r-3xl bg-white group hover:bg-neutralNsf-surface-hover active:bg-neutralNsf-surface-active sm:-right-8 sm:w-12"
            >
              <ChevronRightIcon
                aria-hidden="true"
                className="shrink-0 text-neutralNsf-subtle group-hover:text-neutralNsf group-active:text-neutralNsf"
              />
            </Button>
            <div className="absolute right-10 w-8 h-10 z-10 bg-gradient-to-r from-transparent to-white pointer-events-none sm:right-4 sm:w-10 lg:h-12" />
          </>
        )}
        <ul
          ref={subMenuRef}
          className="flex flex-row gap-3 justify-between items-start w-full h-full pr-6 overflow-x-scroll scroll-smooth scrollbar-hidden sm:gap-6 xl:gap-8"
        >
          {menupages &&
            menupages.map((page, index) => {
              return (
                <li
                  key={`${index}-${page.title}`}
                  className="shrink-0 first:pl-0 p-2 !mb-0"
                >
                  <Link
                    href={page.url}
                    className="flex items-center no-underline text-md font-medium outline-[3px] hover:underline focus-visible:outline-focusOuter focus-visible:outline"
                  >
                    {page.title}
                  </Link>
                </li>
              );
            })}
        </ul>
      </div>
    </nav>
  );
};

export default SubMenuBlock;
